import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Calendar/ui/dialog";
import { Button } from "@/components/Calendar/ui/button";
import { ExternalLink, Clock, Calendar, Zap } from "lucide-react";
import {
  addScheduleEvent,
  refreshScheduleEventsSilently,
} from "@/dispatchers/scheduleEventsDispatchers.js";
import { useTitanApp } from "@/hooks/useTitanApp";

type GoogleCalendarEvent = {
  id: string;
  summary: string;
  description?: string;
  date: string;
  time: string;
  duration: number;
  htmlLink?: string;
  organizer?: string;
  attendees?: string[];
  location?: string;
  status?: string;
  resourceId?: string;
  googleEventId: string;
  startDateTime: string;
  endDateTime: string;
};

interface GoogleEventDetailsModalProps {
  event: GoogleCalendarEvent | null;
  isOpen: boolean;
  isDarkMode: boolean;
  onClose: () => void;
}

export function GoogleEventDetailsModal({
  event,
  isOpen,
  isDarkMode,
  onClose,
}: GoogleEventDetailsModalProps) {
  const { appState, scheduleEventState } = useTitanApp();
  const { currentPharmacyId } = appState || {};
  const { isEventCalendarLoading } = scheduleEventState || {};
  if (!event) return null;

  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(":").map(Number);
      return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return time;
    }
  };

  const endTime = (() => {
    try {
      const [hours, minutes] = event.time.split(":").map(Number);
      const date = new Date(2000, 0, 1, hours, minutes);
      date.setMinutes(date.getMinutes() + event.duration);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  })();

  const handleAddToCalendar = async () => {
    try {
      const payload = {
        id: event.googleEventId,
        StartDate: event.startDateTime,
        EndDate: event.endDateTime,
        Duration: event.duration,
        DurationUnit: "Minutes",
        Cls: null,
        Name: event.summary,
        RecurrenceRule: null,
        ExceptionDates: [],
        ResourceId: event.resourceId,
        ClinicalServiceID: null,
        AllDay: false,
        ScheduleEventType: 1,
        ServiceID: "",
        PatientID: "",
        PatientName: "",
        Email: null,
        Mobile: null,
        ConfirmationMethod: null,
        ConsultationID: null,
        RoomId: null,
        EventStatus: 1,
        Cc: null,
        TasksToComplete: [],
        VaccinesAdminister: [],
        BookingReference: null,
      };

      await addScheduleEvent(payload, currentPharmacyId);
      await refreshScheduleEventsSilently(currentPharmacyId);
      onClose();
    } catch (error) {
      console.error("Failed to add schedule event:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`w-[95vw] sm:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border shadow-2xl ${
          isDarkMode
            ? "bg-gray-900 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Header */}
        <DialogHeader
          className={`pb-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-base font-bold flex-shrink-0 ${
                isDarkMode ? "bg-red-400 text-black" : "bg-red-600 text-white"
              }`}
              title="Google event"
            >
              G
            </span>

            <DialogTitle className="flex-1 text-xl font-semibold break-words">
              {event.summary}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="space-y-6 py-6">
          {/* Time */}
          <div className="flex items-start gap-4">
            <Clock
              className={`w-5 h-5 mt-1 flex-shrink-0 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <div>
              <p className="text-base font-semibold">
                {formatTime(event.time)} – {endTime}
              </p>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {event.duration} minutes
              </p>
            </div>
          </div>

          {/* Date */}
          <div className="flex items-start gap-4">
            <Calendar
              className={`w-5 h-5 mt-1 flex-shrink-0 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <div>
              <p
                className={`text-sm mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Date
              </p>
              <p className="text-base font-semibold">{event.date}</p>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div className="flex items-start gap-4">
              <Zap
                className={`w-5 h-5 mt-1 flex-shrink-0 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />

              <div className="flex-1">
                <p
                  className={`text-sm font-semibold mb-3 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Details
                </p>

                <div
                  className={`rounded-xl border shadow-sm px-5 py-4 transition-colors ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-slate-50 border-gray-200"
                  }`}
                >
                  <div
                    className={`prose prose-sm max-w-none
            prose-p:my-2
            prose-br:leading-7
            prose-strong:font-semibold
            prose-ul:my-2
            prose-ol:my-2
            prose-li:my-1
            prose-a:text-blue-600 hover:prose-a:underline
            ${
              isDarkMode
                ? "prose-invert prose-headings:text-white prose-p:text-gray-200 prose-strong:text-white prose-li:text-gray-200"
                : "prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-li:text-gray-700"
            }`}
                    dangerouslySetInnerHTML={{ __html: event.description }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-4">
              <Zap
                className={`w-5 h-5 mt-1 flex-shrink-0 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <div className="flex-1">
                <p
                  className={`text-sm mb-1 font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Location
                </p>
                <p className="text-base break-words">{event.location}</p>
              </div>
            </div>
          )}

          {/* Organizer */}
          {event?.organizer && (
            <div className="flex items-start gap-4">
              <Zap
                className={`w-5 h-5 mt-1 flex-shrink-0 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <div className="flex-1">
                <p
                  className={`text-sm mb-1 font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Organizer
                </p>
                <p className="text-base break-words">{event.organizer}</p>
              </div>
            </div>
          )}

          {/* Attendee */}
          {event?.resourceId && (
            <div className="flex items-start gap-4">
              <Zap
                className={`w-5 h-5 mt-1 flex-shrink-0 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />

              <div className="flex-1">
                <p
                  className={`text-sm mb-2 font-medium ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Attendee (1)
                </p>

                <div
                  className={`rounded-lg px-3 py-2 ${
                    isDarkMode
                      ? "bg-gray-800 border border-gray-700"
                      : "bg-gray-50 border border-gray-200"
                  }`}
                >
                  {event.resourceId}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex flex-col sm:flex-row gap-3 pt-5 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          {event.htmlLink && (
            <Button
              variant="outline"
              size="default"
              onClick={() => window.open(event.htmlLink, "_blank")}
              className="flex-1 h-11 gap-2"
              disabled={isEventCalendarLoading}
            >
              <ExternalLink className="w-4 h-4" />
              Open in Google Calendar
            </Button>
          )}
          <Button
            variant="outline"
            size="default"
            onClick={() => onClose()}
            className="flex-1 h-11 gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Close
          </Button>
          {/* <Button
            variant="default"
            size="default"
            className="flex-1 h-11 gap-2"
            onClick={handleAddToCalendar}
            disabled={isEventCalendarLoading}
          >
            <ExternalLink className="w-4 h-4" />
            {isEventCalendarLoading ? "Adding..." : "Add to Calendar"}
          </Button> */}
        </div>
      </DialogContent>
    </Dialog>
  );
}
